import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ConversationService } from '../../../../../../core/src/lib/state/conversation.service';
import {
  VideoMessageState,
  VIDEO_STATE_METADATA_KEY,
} from '../../../../../../core/src/lib/interface/video-message-state.enum';
import { ConversationMessage } from '../../../../../../core/src/lib/interface/conversation.interface';
import { ConversationChannel, MessageType, Participant, ParticipantType } from '@vendasta/conversation';
import { MOCK_VIDEO_URL } from './mock-video-utils';

// TODO(TIF-21 Phase 1): Remove MockVideoPanelComponent — mock scaffolding for video transcoding pipeline
@Component({
  selector: 'inbox-mock-video-panel',
  imports: [CommonModule, MatButtonModule, MatIconModule],
  templateUrl: './mock-video-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mock-video-panel.component.scss',
})
export class MockVideoPanelComponent {
  private readonly conversationService = inject(ConversationService);
  private mockCounter = 0;

  protected readonly terminalStates: { label: string; state: VideoMessageState }[] = [
    { label: 'Sent', state: VideoMessageState.Sent },
    { label: 'Failed', state: VideoMessageState.Failed },
    { label: 'Upload Rejected', state: VideoMessageState.UploadRejected },
    { label: 'Transcoding Failed', state: VideoMessageState.TranscodingFailed },
    { label: 'Output Too Large', state: VideoMessageState.OutputTooLarge },
    { label: 'Twilio Rejected', state: VideoMessageState.TwilioRejected },
  ];

  protected readonly stuckStates: { label: string; state: VideoMessageState }[] = [
    { label: 'Processing', state: VideoMessageState.Processing },
    { label: 'Sending', state: VideoMessageState.Sending },
  ];

  triggerScenario(targetState: VideoMessageState) {
    const messageId = `mock-video-${++this.mockCounter}-${Date.now()}`;

    if (targetState === VideoMessageState.UploadRejected) {
      this.runUploadRejectedScenario(messageId);
      return;
    }

    const message = this.createMockMessage(messageId, VideoMessageState.Processing);
    this.conversationService.addMockUnsavedMessage(message);

    if (targetState === VideoMessageState.Processing) return;
    if (targetState === VideoMessageState.Sending) { this.runSendingScenario(messageId); return; }
    if (targetState === VideoMessageState.Sent) { this.runSentScenario(messageId); return; }
    if (targetState === VideoMessageState.TwilioRejected) { this.runTwilioRejectedScenario(messageId); return; }

    this.runDelayedErrorScenario(messageId, targetState);
  }

  private runSendingScenario(messageId: string) {
    setTimeout(() => this.transitionTo(messageId, VideoMessageState.Sending), 1000);
  }

  private runSentScenario(messageId: string) {
    setTimeout(() => this.transitionTo(messageId, VideoMessageState.Sending), 1000);
    setTimeout(() => this.transitionTo(messageId, VideoMessageState.Sent), 2000);
  }

  private runTwilioRejectedScenario(messageId: string) {
    setTimeout(() => this.transitionTo(messageId, VideoMessageState.Sending), 1000);
    setTimeout(() => this.transitionTo(messageId, VideoMessageState.TwilioRejected), 2000);
  }

  private runUploadRejectedScenario(messageId: string) {
    const message = this.createMockMessage(messageId, VideoMessageState.UploadInProgress);
    this.conversationService.addMockUnsavedMessage(message);
    setTimeout(() => this.transitionTo(messageId, VideoMessageState.UploadRejected), 1000);
  }

  private runDelayedErrorScenario(messageId: string, targetState: VideoMessageState) {
    setTimeout(() => this.transitionTo(messageId, targetState), 2000);
  }

  private transitionTo(messageId: string, state: VideoMessageState) {
    this.conversationService.updateMockUnsavedMessage(messageId, {
      metadata: [{ key: VIDEO_STATE_METADATA_KEY, value: state }],
      ...(state === VideoMessageState.Sent
        ? {
            media: [
              {
                mediaUrl: MOCK_VIDEO_URL,
                mediaContentType: 'video/mp4',
                mediaFileName: 'video.mp4',
                mediaLocationPath: '',
              },
            ],
          }
        : {}),
    });
  }

  private createMockMessage(messageId: string, initialState: VideoMessageState): ConversationMessage {
    return {
      id: messageId,
      messageId,
      type: MessageType.MESSAGE_TYPE_MESSAGE,
      channel: ConversationChannel.CONVERSATION_CHANNEL_SMS,
      created: new Date(),
      sender: new Participant({
        participantType: ParticipantType.PARTICIPANT_TYPE_IAM_USER,
        name: 'You',
      }),
      metadata: [{ key: VIDEO_STATE_METADATA_KEY, value: initialState }],
      media: [
        {
          mediaUrl: 'mock://video',
          mediaContentType: 'video/mp4',
          mediaFileName: 'video.mp4',
          mediaLocationPath: '',
        },
      ],
    };
  }
}
